import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Plus, MoreHorizontal, Download, Upload } from 'lucide-react';
import ContextMenu from '../ui/ContextMenu';
import { CALENDAR_VIEWS, CALENDAR_FILTERS } from '../../extensions/bundled/calendar/calendarUiStore';
import { formatRangeTitle } from '../../utils/calendarDates';
import LocalCapHint from '../plans/LocalCapHint';

// KEY tables, resolved at RENDER. A module-scope `t()` would freeze English at
// import time and never follow a language switch (30.08.2026 gotcha).
const VIEW_LABEL_KEYS = {
  month: 'calendar.view.month',
  week: 'calendar.view.week',
  day: 'calendar.view.day',
  agenda: 'calendar.view.agenda',
};
const FILTER_LABEL_KEYS = {
  all: 'calendar.filter.all',
  personal: 'calendar.filter.personal',
  team: 'calendar.filter.team',
};

/**
 * Toolbar: Today · prev/next · range title · Personal/Team/All filter · view
 * switcher (M/W/D/A) · New event · a "⋯" actions menu with Import/Export .ics
 * (fix round 3 — the sidebar is gone, owner placement: right of New event,
 * ContextMenu precedent from the workspace rows' actions menu). Navigation
 * math and the .ics handlers live in CalendarView; this stays presentation.
 */
export default function CalendarToolbar({
  view,
  focusedDate,
  calendarFilter = 'all',
  onToday,
  onNavigate,
  onSetView,
  onSetCalendarFilter,
  onNewEvent,
  onImportIcs,
  onExportIcs,
}) {
  const { t } = useTranslation();
  const [actionsMenu, setActionsMenu] = useState(null); // { x, y }

  return (
    <div className="cal-toolbar">
      <button type="button" className="cal-btn" onClick={onToday}>
        {t('calendar.today')}
      </button>
      <button
        type="button"
        className="cal-btn cal-btn--icon"
        aria-label={t('calendar.previous')}
        onClick={() => onNavigate(-1)}
      >
        <ChevronLeft size={14} />
      </button>
      <button
        type="button"
        className="cal-btn cal-btn--icon"
        aria-label={t('calendar.next')}
        onClick={() => onNavigate(1)}
      >
        <ChevronRight size={14} />
      </button>
      <h3 className="cal-toolbar-title">{formatRangeTitle(view, focusedDate)}</h3>

      {/* Right side. The filter mirrors the view switcher's segmented shape —
          the in-file precedent the owner asked to match. */}
      <div
        className="cal-view-switch"
        role="group"
        aria-label={t('calendar.calendarFilter')}
        style={{ marginLeft: 'auto' }}
      >
        {CALENDAR_FILTERS.map((f) => (
          <button
            key={f}
            type="button"
            aria-pressed={calendarFilter === f}
            onClick={() => onSetCalendarFilter?.(f)}
          >
            {t(FILTER_LABEL_KEYS[f])}
          </button>
        ))}
      </div>
      <div className="cal-view-switch" role="group" aria-label={t('calendar.calendarView')} style={{ marginLeft: 0 }}>
        {CALENDAR_VIEWS.map((v) => (
          <button
            key={v}
            type="button"
            aria-pressed={view === v}
            onClick={() => onSetView(v)}
          >
            {t(VIEW_LABEL_KEYS[v])}
          </button>
        ))}
      </div>
      <button type="button" className="cal-btn cal-btn--primary" onClick={onNewEvent}>
        <Plus size={14} />
        {t('calendar.newEvent')}
      </button>
      <LocalCapHint kind="calendarEvents" />
      <button
        type="button"
        className="cal-btn cal-btn--icon"
        aria-label={t('calendar.moreActions')}
        aria-haspopup="menu"
        onClick={(e) => {
          // Anchored to the button, not the pointer (WorkspaceManager
          // precedent: keyboard-opened clicks carry 0,0).
          const r = e.currentTarget.getBoundingClientRect();
          setActionsMenu({ x: r.right, y: r.bottom + 4 });
        }}
      >
        <MoreHorizontal size={14} />
      </button>

      {actionsMenu && (
        <ContextMenu
          x={actionsMenu.x}
          y={actionsMenu.y}
          items={[
            { icon: <Download size={13} />, label: t('calendar.importIcs'), onClick: () => onImportIcs?.() },
            { icon: <Upload size={13} />, label: t('calendar.exportIcs'), onClick: () => onExportIcs?.() },
          ]}
          onClose={() => setActionsMenu(null)}
        />
      )}
    </div>
  );
}
